import { faFileDownload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { inMarketTheme } from '@inmarket/inmarkit';
import { Button, CircularProgress, ClickAwayListener, Paper, Popper, Stack, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { getLatestReport } from '../../api/ventura/contract';
import { useConfig } from '../../hooks/useConfig';
import { useSnackBar } from '../../hooks/useSnackBar';
import { camelCaseKeys } from '../../util/object';
import { useContractsTableContext } from './useContractsTableContext';

interface ContractLatestReportButtonProps {
  contractId: number;
  index?: number;
  // When true, render the legacy icon-only trigger (used in tight cells like the contract-list grid).
  compact?: boolean;
}

interface LatestReportTypeProps {
  contractId: number;
  name: string;
  type: number;
}

function LatestReportType({ contractId, name, type }: LatestReportTypeProps) {
  const { apiHost } = useConfig();
  const { showSnackBar } = useSnackBar();
  const [isLoading, setIsLoading] = useState(false);
  const [downloadIconColor, setDownloadIconColor] = useState('rgba(0, 0, 0, 0.54)');

  const getLatestReportUrl = async (reportType) => {
    setIsLoading(true);
    const rawData = await getLatestReport(apiHost, contractId, reportType);
    setIsLoading(false);
    const data = camelCaseKeys(rawData);

    if (data?.errors) {
      showSnackBar(data.errors[0], 'error', { event: 'report_download_error', contract_id: contractId, report_type: reportType });
      setDownloadIconColor(inMarketTheme.palette.error.main);
    }
    if (data?.reports) {
      window.open(data.reports);
      showSnackBar('The report was downloaded successfully.', 'success', { event: 'report_downloaded', contract_id: contractId, report_type: reportType });
      setDownloadIconColor(inMarketTheme.palette.primary.main);
    }
  };

  return (
    <Stack direction="row" spacing={2} justifyContent="space-between">
      <Typography>{name}</Typography>
      <Stack justifyContent="center">
        {isLoading && <CircularProgress color="warning" size="1rem" />}
        {!isLoading && (
          <FontAwesomeIcon
            icon={faFileDownload}
            color={downloadIconColor}
            onClick={() => getLatestReportUrl(type)}
            style={{ cursor: 'pointer' }}
          />
        )}
      </Stack>
    </Stack>
  );
}

function ContractLatestReportButton({ contractId, index, compact = false }: ContractLatestReportButtonProps) {
  const contractLatestReportContextValue = useContractsTableContext();
  const { activeIndex, setActiveIndex } = contractLatestReportContextValue || {};

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const togglePopper = (e: React.MouseEvent<Element>) => {
    e.stopPropagation();
    if (anchorEl) {
      setAnchorEl(null);
      if (setActiveIndex) {
        setActiveIndex(undefined);
      }
    } else {
      setAnchorEl(e.currentTarget as HTMLElement);
      if (setActiveIndex) {
        setActiveIndex(index);
      }
    }
  };

  useEffect(() => {
    if (activeIndex !== index) {
      setAnchorEl(null);
    }
  }, [index, activeIndex]);

  const reportTypes = ['Standard', '3P Standard', 'Geo', '3P Geo'];

  const trigger = compact ? (
    <Tooltip title="Download Report" placement="top-end">
      <FontAwesomeIcon icon={faFileDownload} color="rgba(0, 0, 0, 0.54)" onClick={togglePopper} />
    </Tooltip>
  ) : (
    <Button
      variant="outlined"
      color="success"
      startIcon={<FontAwesomeIcon icon={faFileDownload} />}
      onClick={togglePopper}
    >
      Download Report
    </Button>
  );

  return (
    <Stack sx={{ color: 'gray' }}>
      {trigger}
      <Popper
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        placement="top-start"
        onClick={(e) => e.stopPropagation()}
        sx={{ zIndex: 10 }}
      >
        <ClickAwayListener
          onClickAway={() => {
            setAnchorEl(null);
            if (setActiveIndex) {
              setActiveIndex(undefined);
            }
          }}
        >
          <div>
            <Paper sx={{ p: 2 }}>
              <Stack>
                {reportTypes.map((reportType, index2) => (
                  <LatestReportType key={reportType} contractId={contractId} name={reportType} type={index2} />
                ))}
              </Stack>
            </Paper>
          </div>
        </ClickAwayListener>
      </Popper>
    </Stack>
  );
}

export default ContractLatestReportButton;
